import _ from 'lodash'
import type { ViewerPlatformEssentials } from '@wix/fe-essentials-viewer-platform'
import type { ControllerDataAPI, LivePreviewOptions, IModelsAPI, ClientSpecMapAPI } from '@wix/thunderbolt-symbols'
import type { IWixSelector } from './wixSelector'
import type { ISetPropsManager } from './setPropsManager'
import type { ISlotsManager } from './slotsManager'
import type { ControllerData } from '../types'
import type { IAppsUrls } from './appsUrls'
import type { CreateSingleRepeatedControllerFn } from './applications'
import { extractArtifactIdFromScriptUrl } from '../appsAPI/extractArtifactIdFromScriptUrl'
import { APPS_URLS, BOOTSTRAP_DATA, CLIENT_SPEC_MAP_API, CONTROLLER_PARAMS, MODELS_API, PLATFORM_ESSENTIALS, SET_PROPS_MANAGER, SLOTS_MANAGER, WIX_SELECTOR } from './moduleNames'
import { AppState, BootstrapData } from '../../types'

export type ControllersParams = Array<{ controllerCompId: string; controllerParams: ControllerDataAPI }>

export type IControllerParams = {
	createControllersParams: (
		appDefinitionId: string,
		controllersData: Array<ControllerData>,
		appState: AppState,
		createSingleRepeatedController: CreateSingleRepeatedControllerFn,
		livePreviewOptions?: Partial<LivePreviewOptions>,
		isRepeatedSlots?: boolean
	) => ControllersParams
}

const ControllerParams = (
	modelsApi: IModelsAPI,
	{ createSetPropsForOOI }: ISetPropsManager,
	wixSelector: IWixSelector,
	slotsManager: ISlotsManager,
	clientSpecMapApi: ClientSpecMapAPI,
	bootstrapData: BootstrapData,
	essentials: ViewerPlatformEssentials,
	appsUrls: IAppsUrls
): IControllerParams => {
	const getSlotByName = modelsApi.getSlotByName
	const {
		platformEnvData: {
			window: { csrfToken },
		},
	} = bootstrapData

	const createControllersParams: IControllerParams['createControllersParams'] = (
		appDefinitionId,
		controllersData,
		appState,
		createSingleRepeatedController,
		livePreviewOptions?,
		isRepeatedSlots?
	) => {
		const connections = modelsApi.getAllConnections()
		const widgetsClientSpecMapData = clientSpecMapApi.getWidgetsClientSpecMapData(appDefinitionId)
		const { appParams, wixCodeApi, platformAppServicesApi, platformApi, platformApiProvider } = appState
		const appEssentials = platformAppServicesApi.essentials

		return controllersData.map((controllerData) => {
			const { controllerType, compId, templateId, config, externalId, context } = controllerData
			const $wScope = context
				? wixSelector.create$wRepeaterScope({
						compId: context._internal.repeaterCompId,
						itemId: context.itemId,
				  })
				: wixSelector.create$wGlobalScope()

			const clientSpecMapData = widgetsClientSpecMapData[controllerType]

			const controllerScriptUrl = appsUrls.getControllerScriptUrl(appDefinitionId, controllerType)

			const controllerArtifactId = extractArtifactIdFromScriptUrl(controllerScriptUrl)

			return {
				controllerCompId: compId,
				controllerParams: {
					$w: context ? wixSelector.create$w(compId, wixCodeApi.telemetry).at(context, isRepeatedSlots) : wixSelector.create$w(compId, wixCodeApi.telemetry),
					getSlot: (slotName) => slotsManager.getSlot(compId, compId, slotName, wixSelector.getInstance, $wScope),
					compId: templateId || compId,
					name: clientSpecMapData?.widgetName || controllerType,
					componentFields: clientSpecMapData?.componentFields || {},
					type: controllerType,
					config,
					connections: _.flatMap(connections[compId], _.values),
					warmupData: null,
					appParams,
					platformAPIs: Object.assign(platformApi, platformAppServicesApi),
					wixCodeApi,
					csrfToken,
					setProps: createSetPropsForOOI(compId, context),
					externalId,
					essentials: essentials.createControllerEssentials(
						{
							widgetId: controllerType,
							compId,
							artifactId: controllerArtifactId,
						},
						appEssentials
					),
					livePreviewOptions,
					platformApiProvider,
					createRepeatedSlotAPI: (slotName: string, repeatedItemId: string) => {
						const slotId = getSlotByName(compId, slotName)
						if (!slotId) {
							return Promise.resolve({})
						}

						return createSingleRepeatedController(slotName, slotId, repeatedItemId).then(async (runControllersOnReadys: Function) => {
							await runControllersOnReadys()
							const local$wScope = wixSelector.create$wRepeaterScope({ compId: slotId, itemId: repeatedItemId })
							return slotsManager.getSlot(compId, compId, slotName, wixSelector.getInstance, local$wScope, repeatedItemId)
						})
					},
				},
			}
		})
	}

	return {
		createControllersParams,
	}
}

export default {
	factory: ControllerParams,
	deps: [MODELS_API, SET_PROPS_MANAGER, WIX_SELECTOR, SLOTS_MANAGER, CLIENT_SPEC_MAP_API, BOOTSTRAP_DATA, PLATFORM_ESSENTIALS, APPS_URLS],
	name: CONTROLLER_PARAMS,
}
